/// <reference types="react" />

/// <amd-module name="Repos/CommitsList/Components/HistoryGraphRow" />
import * as React from "react";
import { ICommit } from "Repos/Common/Contracts/History";
import { IVssComponentContext, VssComponent } from "VSS/Platform/Layout";
import { IVssThemeService } from "VSS/Platform/Theming";
import { HistoryGraphCell } from "./HistoryGraphCell";
import { HistoryGraphOrientation, IHistoryGraphRow } from "./HistoryGraphCell.Props";
import { HistoryGraphCellRenderer } from "./HistoryGraphCellRenderer";

export interface IHistoryGraphRowProps {
    commit: ICommit;
    row: IHistoryGraphRow | undefined;
    staticWidth: number;
    staticHeight: number;
}

export interface IHistoryGraphRowState {
    currentHeight: number;
}

/**
 * Renders a single git graph row
 */
export class HistoryGraphRow extends VssComponent<IHistoryGraphRowProps, IHistoryGraphRowState> {
    private row = React.createRef<HTMLDivElement>();
    private cellRenderer: HistoryGraphCellRenderer;

    constructor(props: IHistoryGraphRowProps, context?: IVssComponentContext) {
        super(props, context);

        const themeService = this.context.pageContext.getService<IVssThemeService>("IVssThemeService");
        const highlightColor = `rgb(${themeService.getThemeValue("palette-primary-shade-10")})`;
        const defaultColor = `rgb(${themeService.getThemeValue("palette-neutral-60")})`;

        this.cellRenderer = new HistoryGraphCellRenderer(HistoryGraphOrientation.RightJustify, {
            /* Node settings */
            nodeStrokeWidth: 2,
            mergeStrokeWidth: 2,
            nodeRadius: 4,
            mergeRadius: 4,
            nodeHighlightRadius: 4,
            mergeHighlightRadius: 4,

            nodeColor: highlightColor,
            mergeColor: defaultColor,
            nodeHighlightColor: highlightColor,
            mergeHighlightColor: defaultColor,
            nodeFillColor: highlightColor,
            mergeFillColor: defaultColor,
            nodeHighlightFillColor: highlightColor,
            mergeHighlightFillColor: defaultColor,

            /* Line settings */
            lineWidth: 2,
            lineHighlightWidth: 2,
            lineColor: defaultColor,
            lineHighlightColor: highlightColor,

            /* Cell settings */
            cellStaticWidth: 16,
            cellStaticHeight: 50
        });

        this.state = { currentHeight: props.staticHeight };
    }

    public render(): JSX.Element {
        if (this.props.row) {
            const heightWithoutBorders = this.state.currentHeight - 2;
            return (
                <div className="graph-row" ref={this.row} style={{ height: `${heightWithoutBorders}px` }}>
                    {this._getGraphCellCanvases()}
                </div>
            );
        } else {
            return <div />;
        }
    }

    public componentDidUpdate(prevProps: IHistoryGraphRowProps, prevState: IHistoryGraphRowState): void {
        if (this.row.current && prevState.currentHeight === this.state.currentHeight) {
            const parent = this.row.current.offsetParent as HTMLElement;
            if (parent) {
                const newHeight = Math.round(parent.offsetHeight);

                if (newHeight !== this.state.currentHeight) {
                    this.setState({
                        currentHeight: newHeight
                    });
                }
            }
        }
    }

    public componentDidMount(): void {
        if (this.row.current) {
            const parent = this.row.current.offsetParent as HTMLElement;
            if (parent) {
                const newHeight = Math.round(parent.offsetHeight);

                if (newHeight !== this.state.currentHeight) {
                    this.setState({
                        currentHeight: newHeight
                    });
                }
            }
        }
    }

    /**
     * Returns the array of graph cell elements that needs to be rendered in the row
     * 1. Creates the graph cell element for the row's tracing cell
     * 2. Creates the graph cell element for the row's excision cell
     * 3. Creates graph cell elements for all cells in the graph row model
     */
    private _getGraphCellCanvases(): JSX.Element[] {
        const graphCells: JSX.Element[] = [];
        let maxCellId = 0;

        if (this.props.row && this.props.commit.commit) {
            for (const key in this.props.row.cells) {
                const cellIndex = parseInt(key);
                if (!isNaN(cellIndex)) {
                    maxCellId = cellIndex;
                }
            }

            // create the graph cell element for the row's tracing cell
            const commitId = this.props.commit.commit.commitId;
            const tracingCellId = "cell-" + commitId + "-tracing";

            graphCells.push(
                <HistoryGraphCell
                    key={tracingCellId}
                    cellId={tracingCellId}
                    cell={this.props.row.getTracingCell()}
                    cellRenderer={this.cellRenderer}
                    width={this.props.staticWidth}
                    height={this.props.staticHeight}
                    extendedHeight={this.state.currentHeight}
                />
            );

            // create the graph cell element for the row's excision cell
            const excisionCellId = "cell-" + commitId + "-excision";
            graphCells.push(
                <HistoryGraphCell
                    key={excisionCellId}
                    cellId={excisionCellId}
                    cell={this.props.row.getExcisionCell()}
                    cellRenderer={this.cellRenderer}
                    width={this.props.staticWidth}
                    height={this.props.staticHeight}
                    extendedHeight={this.state.currentHeight}
                />
            );

            // create the graph cell elements for all the cells in the row model
            for (let i = 0; i <= maxCellId; i++) {
                const cellId = "cell-" + commitId + "-" + i;
                graphCells.push(
                    <HistoryGraphCell
                        key={cellId}
                        cellId={cellId}
                        cell={this.props.row.cells[i]}
                        cellRenderer={this.cellRenderer}
                        width={this.props.staticWidth}
                        height={this.props.staticHeight}
                        extendedHeight={this.state.currentHeight}
                    />
                );
            }
        }
        return graphCells;
    }
}
